<script setup lang="ts">
import { unzipSync, strFromU8 } from "fflate";
import { load } from "cheerio";
import { defineProps, toRef } from "vue";
import { useQuery } from "@tanstack/vue-query";
import LoadingIndicator from "../general/LoadingIndicator.vue";
import { ErrorState } from "../states";
import logger from "../../lib/logger";

const props = defineProps<{ queryKey: number | number; zipUrl: string }>();

const unzipFile = (zipData: ArrayBuffer) => {
  const unzippedFiles = unzipSync(new Uint8Array(zipData));

  const indexFile = unzippedFiles["index.html"];
  if (!indexFile) {
    logger.error("index.html not found in the zip file.", {
      zipUrl: props.zipUrl,
    });
    throw new Error("index.html not found in the zip file.");
  }

  const decodedData = strFromU8(indexFile);
  const $ = load(decodedData);
  const body = $("body");

  body.find("img").each((_, element) => {
    const $element = $(element);
    const src = $element.attr("src");
    if (src && unzippedFiles[src]) {
      const blob = new Blob([unzippedFiles[src]]);
      const url = URL.createObjectURL(blob);
      $element.attr("src", url);
    }
  });

  return body.html();
};

const { data, isLoading, isError, refetch } = useQuery({
  queryKey: ["article-zip", toRef(props, "queryKey")],
  queryFn: async () => {
    const res = await fetch(props.zipUrl);
    if (!res.ok) {
      throw new Error("failed to fetch zip file.");
    }
    const zipData = await res.arrayBuffer();
    return unzipFile(zipData);
  },
});
</script>

<template>
  <div>
    <div v-if="isLoading || isError" class="py-4">
      <div class="min-h-40 bg-background-secondary flex-center rounded-xl">
        <LoadingIndicator v-if="isLoading" class="text-icon-brand" />
        <ErrorState v-else-if="isError" @refetch="refetch" />
      </div>
    </div>
    <div v-else-if="data" class="article-content">
      <article v-html="data" />
    </div>
  </div>
</template>
